import { AsteriskIcon, CheckIcon, LoaderCircleIcon } from 'lucide-react'
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { debounce } from 'lodash'
import { UseFormGetValues, UseFormSetValue } from 'react-hook-form'
import './styles.css'
import clsx from 'clsx'
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react'
import { AnimatePresence, motion } from 'motion/react'

type Option = {
  id: number
  name: string
}

type Props = React.InputHTMLAttributes<HTMLInputElement> & {
  label: string
  multiple?: boolean
  name: string
  options: Option[]
  value?: Option | undefined
  initialValue?: Option | undefined
  setValue: UseFormSetValue<any>
  getValues: UseFormGetValues<any>
  disabled?: boolean
  register?: any
  onQueryChange?: (value: string) => void
  icon?: ReactNode
  optionsLoading?: boolean
  debounceMs?: number
  /** When set, an "add" row is shown at the bottom of the list; receives the current query. */
  onAddOption?: (query: string) => void
  /** Label for the "add" row (defaults to `+ Add "<query>"`). */
  addOptionLabel?: (query: string) => string
}
const MIN_LOADING_TIME_MS = 800
export default function ComboBox({
  name,
  label,
  options,
  multiple = false,
  setValue,
  getValues,
  disabled = false,
  initialValue,
  register,
  onQueryChange,
  autoFocus,
  icon,
  optionsLoading = false,
  debounceMs = 0,
  onAddOption,
  addOptionLabel,
  ...inputHtmlProps
}: Props) {
  const [query, setQuery] = useState(initialValue?.name ?? '')
  const [selected, setSelected] = useState<Option | undefined>(initialValue)
  const [filteredOptions, setFilteredOptions] = useState<Option[]>([])
  const [showLoading, setShowLoading] = useState(false)
  const comboboxRef = useRef<HTMLDivElement>(null)
  const loadingStartTimeRef = useRef<number | null>(null)

  const debouncedOnQueryChange = useMemo(() => {
    if (!onQueryChange) return undefined
    return debounce(onQueryChange, debounceMs)
  }, [onQueryChange, debounceMs])

  useEffect(() => {
    return () => {
      debouncedOnQueryChange?.cancel()
    }
  }, [debouncedOnQueryChange])

  useEffect(() => {
    setQuery('')
    setFilteredOptions([])
  }, [selected])

  useEffect(() => {
    if (optionsLoading && !showLoading) {
      loadingStartTimeRef.current = Date.now()
      setShowLoading(true)
      return
    }
    if (!optionsLoading && showLoading) {
      const elapsed = Date.now() - (loadingStartTimeRef.current || 0)
      const remainingTime = Math.max(0, MIN_LOADING_TIME_MS - elapsed)

      if (remainingTime > 0) {
        const timeoutId = setTimeout(() => {
          setShowLoading(false)
          loadingStartTimeRef.current = null
        }, remainingTime)
        return () => clearTimeout(timeoutId)
      } else {
        setShowLoading(false)
        loadingStartTimeRef.current = null
      }
    }
  }, [optionsLoading, showLoading])

  useEffect(() => {
    const filtered =
      query === ''
        ? options
        : options.filter((option) => {
            return option.name.toLowerCase().includes(query.toLowerCase())
          })
    if (filtered !== filteredOptions) {
      setFilteredOptions(filtered)
    }
  }, [options])

  const trimmedQuery = query.trim()
  const showAddOption =
    Boolean(onAddOption) && trimmedQuery.length > 0 && !showLoading

  return (
    <div className="h-fit">
      <Combobox
        immediate
        multiple={multiple}
        value={selected ?? null}
        onChange={(value) => {
          setSelected(value as Option)
          setValue(name, (value as Option) ?? undefined, {
            shouldDirty: true,
          })
        }}
        disabled={disabled}
      >
        <div
          ref={comboboxRef}
          className={clsx(
            'form-field-root flex flex-row gap-1',
            filteredOptions.length > 0 && query.length > 0
              ? 'rounded-b-none'
              : 'rounded-b',
            multiple ? '!rounded-b-none !border-b-0 !pb-0' : '',
            disabled && 'opacity-50 cursor-not-allowed',
          )}
        >
          <div className="flex flex-col gap-1 w-full">
            <input
              type="text"
              className="peer"
              value={selected ? selected.name : query}
              readOnly
              hidden
              {...(register ? register(name) : {})}
              {...inputHtmlProps}
            />
            <label
              htmlFor={name}
              className="text-themed-grey-500 font-bold bg-transparent text-xs"
            >
              <div className="flex flex-row">
                {label}
                {inputHtmlProps.required && (
                  <AsteriskIcon className="text-themed-error" size={12} />
                )}
              </div>
            </label>
            <ComboboxInput
              name={name}
              className={clsx(
                'w-full outline-none !px-0 border-none text-sm text-themed-grey-500 font-medium bg-transparent',
                disabled && 'bg-optiwatt-grey-100',
              )}
              displayValue={(o: Option | undefined) => {
                return o?.name ?? query
              }}
              onChange={(event) => {
                setQuery(event.target.value)
                debouncedOnQueryChange?.(event.target.value)
              }}
              autoFocus={autoFocus}
              disabled={disabled}
            />
          </div>
          <div className="flex items-center justify-center">
            {showLoading ? (
              <LoaderCircleIcon className="size-6 animate-spin text-base-grey-400" />
            ) : (
              <div className="size-6">{icon}</div>
            )}
          </div>
        </div>
        {(filteredOptions.length > 0 || showLoading || showAddOption) && (
          <ComboboxOptions
            anchor="bottom"
            className="border peer border-solid border-themed-grey-200 rounded-b-lg bg-white p-1 translate-x-[14px] [--anchor-gap:3px] z-10"
            style={{
              width: comboboxRef.current?.offsetWidth
                ? `${comboboxRef.current.offsetWidth}px`
                : 'fit',
            }}
          >
            {showLoading ? (
              <AnimatePresence>
                <div className="flex flex-col gap-1">
                  {[0, 1, 2, 3].map((index) => (
                    <motion.div
                      key={index}
                      className="h-10 bg-themed-grey-200 rounded-lg"
                      animate={{
                        opacity: [0.4, 1, 0.4],
                      }}
                      transition={{
                        duration: 0.8,
                        repeat: Infinity,
                        ease: 'easeInOut',
                        delay: index * 0.1,
                      }}
                    />
                  ))}
                </div>
              </AnimatePresence>
            ) : (
              <>
                {filteredOptions.map((option) => (
                  <ComboboxOption
                    key={option.id}
                    value={option}
                    className="group flex bg-white cursor-default items-center justify-center gap-2 py-2 px-3 rounded-lg select-none data-[focus]:bg-themed-grey-100"
                  >
                    <div className="w-4 flex items-center justify-center">
                      <CheckIcon
                        size={16}
                        className="invisible size-4 fill-white group-data-[selected]:visible text-themed-green-700"
                      />
                    </div>
                    <div className="text-sm/6 text-themed-grey-500 grow text">
                      {option.name}
                    </div>
                  </ComboboxOption>
                ))}
                {showAddOption && (
                  <div
                    role="button"
                    tabIndex={0}
                    // onMouseDown (not onClick) so the option fires before the
                    // input blur closes the list.
                    onMouseDown={(event) => {
                      event.preventDefault()
                      onAddOption?.(trimmedQuery)
                    }}
                    className="flex bg-white cursor-pointer items-center gap-2 py-2 px-3 rounded-lg select-none hover:bg-themed-grey-100 text-themed-blue-500 font-bold text-sm/6"
                  >
                    {addOptionLabel
                      ? addOptionLabel(trimmedQuery)
                      : `+ Add "${trimmedQuery}"`}
                  </div>
                )}
              </>
            )}
          </ComboboxOptions>
        )}
      </Combobox>
    </div>
  )
}
